import PropTypes from 'prop-types'

import { connect } from 'react-redux'

import styled from '@emotion/styled'

import { selectCampaignRetailerLogoSrc, selectCampaignRetailerId, selectCampaignRetailerName } from '../../../flux/selectors/campaign'
import { selectHeroSection, selectBrandColorBg } from '../../../flux/selectors/layout'

import { uxEvent } from '../../../flux/actions/ux'

import CustomHeroSection from './CustomHeroSection'
import FallbackHeroSection from './FallbackHeroSection'
import { selectRetailerLogoCover } from '../../../flux/selectors/retailers'
import LoadingSkeleton from '../Generics/LoadingSkeleton'
import { HERO_SECTION_HEIGHT } from '../../../helpers/const'

const StyledRoot = styled.div(({ height }) => ({
  height: height || 'auto',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const StyledContainer = styled.div(({ theme: { breakpoints }, backgroundColor }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  maxWidth: breakpoints.xlarge,
  backgroundColor,
}))

const HeroSection = ({
  custom,
  url,
  desktop,
  tablet,
  mobile,
  retailerLogoSrc,
  brandColorBg,
  retailerName,
  height,
  onHeroSectionClick,
}) => {
  const content = (
    <StyledRoot
      height={custom ? '' : height}
      data-test="HeroSection"
    >
      <StyledContainer
        backgroundColor={custom ? '' : brandColorBg}
        onClick={url ? onHeroSectionClick : () => false}
      >
        { custom ? (
          <CustomHeroSection
            desktop={desktop}
            tablet={tablet}
            mobile={mobile}
            retailerName={retailerName}
          />
        ) : (
          <FallbackHeroSection src={retailerLogoSrc || ''} />
        )}
      </StyledContainer>
    </StyledRoot>
  )

  const isHeroImageAvailable = !!(retailerLogoSrc || (desktop || tablet || mobile))

  const renderHeroSection = () => (url && custom ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {content}
    </a>
  ) : content)

  return isHeroImageAvailable
    ? renderHeroSection()
    : (
      <StyledRoot>
        <StyledContainer>
          <LoadingSkeleton height={`${HERO_SECTION_HEIGHT}px`} />
        </StyledContainer>
      </StyledRoot>
    )
}

HeroSection.propTypes = {
  custom: PropTypes.bool,
  url: PropTypes.string,
  desktop: PropTypes.string,
  tablet: PropTypes.string,
  mobile: PropTypes.string,
  retailerLogoSrc: PropTypes.string,
  brandColorBg: PropTypes.string,
  retailerName: PropTypes.string,
  height: PropTypes.number,
  onHeroSectionClick: PropTypes.func.isRequired,
}

HeroSection.defaultProps = {
  custom: undefined,
  url: null,
  desktop: null,
  tablet: null,
  mobile: null,
  retailerLogoSrc: null,
  brandColorBg: '',
  retailerName: '',
  height: 150,
}

const mapStateToProps = state => {
  const heroSection = selectHeroSection(state)
  const retailerId = selectCampaignRetailerId(state)
  const customRetailerLogo = selectCampaignRetailerLogoSrc(state)
  const retailerLogoCover = selectRetailerLogoCover(retailerId)(state)
  const retailerName = selectCampaignRetailerName(state)
  const brandColorBg = selectBrandColorBg(state)

  return heroSection?.custom ? ({
    custom: true,
    url: heroSection.url,
    mobile: heroSection.mobile,
    desktop: heroSection.desktop,
    tablet: heroSection.tablet,
    retailerName,
  }) : ({
    custom: false,
    url: heroSection?.url,
    brandColorBg,
    retailerLogoSrc: customRetailerLogo || retailerLogoCover,
  })
}

const mapDispatchToProps = {
  uxEvent,
}

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  onHeroSectionClick: () => {
    dispatchProps.uxEvent({
      name: 'click',
      target: 'HeroSection',
    })
  },
})

HeroSection.displayName = 'HeroSection'
HeroSection.whyDidYouRender = true

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(HeroSection)
